import type { ShopperBasketsTypes } from 'commerce-sdk-isomorphic'
import { FormContext } from 'vee-validate'

interface Contact {
  firstName: string
  lastName: string
  email: string
  phone: string
}

interface Search {
  search: string
}

interface DeliveryAddress {
  address1: string
  address2: string
  city: string
  stateCode: string
  postalCode: string
  countryCode: string
}

interface TermsAndConditions {
  isCanada: boolean
  termsAndConditions: boolean
}
export interface ContactForm {
  email: string
  password: string | undefined
  newsletterSubscribe: boolean | null | undefined
  phone: string
  showLogin: boolean | undefined
}

type CheckoutForm = Partial<Contact> & Partial<DeliveryAddress> & Partial<Search>
type AddressFormValues = Pick<Contact, 'firstName' | 'lastName' | 'phone'> & DeliveryAddress & Search

// How long the C&C stock snapshot stays trusted before pay-intent
// validation must re-fetch it (see useCheckoutValidation). (10 minutes)
export const STOCK_STALE_MS = 10 * 60 * 1000

export const useCheckoutStore = defineStore('checkout', () => {
  // Shipping methods
  const applicableShippingMethods = ref<ShopperBasketsTypes.ShippingMethod[]>([])
  const selectedShippingMethod = ref<ShopperBasketsTypes.ShippingMethod>({ id: '', name: '', price: undefined })
  const isClickAndCollect = computed(() => selectedShippingMethod.value.id === CLICK_AND_COLLECT_ID)

  const sameAsShipping = ref<boolean>(true)
  // Address form states
  const isSameAsShipping = computed({
    get() {
      return !isClickAndCollect.value && sameAsShipping.value
    },
    set(value: boolean) {
      sameAsShipping.value = value
    },
  })

  const isEditedShippingAddress = ref<boolean>(false)
  const isEditedBillingAddress = ref<boolean>(false)
  const isMergingBasket = ref(false)
  const showConfirmAddress = ref<boolean>(false)
  const showFindInStore = ref<boolean>(false)
  const addressSuggestions = ref<any[]>([])
  const loading = ref<boolean>(true)
  const clickAndCollectError = ref('')
  const clickAndCollectErrorAttempt = ref(0)
  const warning = ref('')
  const stock = ref<StockResult>()
  const stockFetchedAt = ref(0)
  const setStock = (result?: StockResult) => {
    if (!result) {
      return
    }
    stock.value = result
    stockFetchedAt.value = Date.now()
  }
  const isStockStale = () => Date.now() - stockFetchedAt.value > STOCK_STALE_MS
  const adyenPaymentMethod = ref('')
  const isClickToPay = ref(false)

  const shippingForm = {
    manualEditing: ref(false),
    showAddressInfo: ref(false),
    isEdited: computed({
      get() {
        return !isClickAndCollect.value && isEditedShippingAddress.value
      },
      set(value: boolean) {
        isEditedShippingAddress.value = value
      },
    }),
    backupValues: {} as CheckoutForm,
    veeForm: {} as FormContext<AddressFormValues>,
    bumpAttempt: () => {},
  }

  const billingForm = {
    manualEditing: ref(false),
    showAddressInfo: ref(false),
    isEdited: computed({
      get() {
        return isEditedBillingAddress.value
      },
      set(value: boolean) {
        isEditedBillingAddress.value = value
      },
    }),
    backupValues: {} as CheckoutForm,
    veeForm: {} as FormContext<AddressFormValues>,
    bumpAttempt: () => {},
  }

  const termsAndConditionsForm = {
    veeForm: {} as FormContext<TermsAndConditions>,
    bumpAttempt: () => {},
  }

  const contactForm = {
    veeForm: {} as FormContext<ContactForm>,
    bumpAttempt: () => {},
  }

  // Address forms
  const forms = computed(() => ({
    [CONTACT]: contactForm,
    [SHIPPING]: shippingForm,
    [BILLING]: isSameAsShipping.value ? shippingForm : billingForm,
    [TERMS]: termsAndConditionsForm,
  }))

  const isFormValid = (type: AddressFormType | typeof TERMS | typeof CONTACT) =>
    forms.value[type].veeForm.meta.value.valid

  const areFormsValid = () => {
    if (isClickAndCollect.value) return isFormValid(BILLING) && isFormValid(TERMS) && isFormValid(CONTACT)
    return isFormValid(SHIPPING) && isFormValid(BILLING) && isFormValid(TERMS) && isFormValid(CONTACT)
  }

  const getFirstErrorField = () => {
    for (const [id, form] of Object.entries(forms.value)) {
      const keys = Object.keys(form.veeForm.errors.value)
      if (keys.length > 0) {
        return { id: `${id}AddressForm`, name: keys[0] }
      }
    }
    return null
  }

  const toggleManualEdit = (type: AddressFormType, value?: boolean) => {
    forms.value[type].manualEditing.value = value !== undefined ? value : !forms.value[type].manualEditing.value
    if (forms.value[type].manualEditing.value) {
      // Save form values before editing in case of cancel
      const v = forms.value[type].veeForm.values
      forms.value[type].backupValues = {
        firstName: v.firstName || '',
        lastName: v.lastName || '',
        address1: v.address1 || '',
        address2: v.address2 || '',
        city: v.city || '',
        stateCode: v.stateCode || '',
        postalCode: v.postalCode || '',
        countryCode: v.countryCode || '',
      }
    }
    forms.value[type].showAddressInfo.value = !forms.value[type].manualEditing.value
  }

  return {
    applicableShippingMethods,
    selectedShippingMethod,
    isClickAndCollect,

    forms,
    isFormValid,
    areFormsValid,
    getFirstErrorField,
    toggleManualEdit,

    isSameAsShipping,
    isMergingBasket,
    showConfirmAddress,
    showFindInStore,
    addressSuggestions,
    clickAndCollectError,
    clickAndCollectErrorAttempt,
    warning,
    loading,
    stock,
    stockFetchedAt,
    setStock,
    isStockStale,
    adyenPaymentMethod,
    isClickToPay,
  }
})
